import React, {useState, useMemo} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faReceipt,
    faLock,
    faCalendarAlt,
    faChartPie,
    faUsers,
    faGift,
    faUserEdit,
    faMoneyBillWave,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {Expense, EventMeta, SettlementConfig, SettlementStatusItem, SponsorExpense} from '../../types';
import {formatDate, startOfDay} from '../../utils/helpers';
import ExpenseList, {ReorderPayload} from "../ExpenseList";
import ExpenseForm from "../forms/ExpenseForm";
import SponsorForm from "../forms/SponsorForm";
import ExportButton from './ExportButton';
import {useExpenses} from '../../hooks/useExpenses';
import {useTranslation} from 'react-i18next';
import {useFormatCurrency} from '../../hooks/useFormatCurrency';
import {useSettlementPlan} from '../../hooks/useSettlementPlan';

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

interface ExpenseSectionProps {
    expenses: (Expense | SponsorExpense)[];
    participants: { [userId: string]: string };
    selectedParticipantId: string;
    isEventClosed: boolean;
    eventId: string;
    eventOwnerId: string;
    meta: EventMeta;
    // Read-only here: the summary reports how far the plan has got, the payments
    // tab is where it is drawn up and marked off.
    settlementStatus: { [settlementKey: string]: SettlementStatusItem };
    settlementConfig?: SettlementConfig;
}

const ExpenseSection: React.FC<ExpenseSectionProps> = ({
                                                           expenses,
                                                           participants,
                                                           selectedParticipantId,
                                                           isEventClosed,
                                                           eventId,
                                                           eventOwnerId,
                                                           meta,
                                                           settlementStatus,
                                                           settlementConfig
                                                       }) => {
    const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
    const [editingSponsor, setEditingSponsor] = useState<SponsorExpense | null>(null);
    const [activeTab, setActiveTab] = useState<'expense' | 'sponsor'>('expense');
    const [selectedPaidBy, setSelectedPaidBy] = useState<string>(''); // Payer dropdown filter
    const [activeQuickFilter, setActiveQuickFilter] = useState<'involvingMe' | 'createdByMe' | 'paidByMe' | ''>(''); // Quick filter chip
    const {updateExpense, deleteExpense, loading} = useExpenses();
    const {t} = useTranslation();
    const {formatCurrency} = useFormatCurrency();

    // ✅ Calculate expenses and sponsorships separately
    const actualExpenses = expenses.filter(exp => exp.type !== 'sponsor') as Expense[];
    const sponsorships = expenses.filter(exp => exp.type === 'sponsor') as SponsorExpense[];

    // ✅ Filter expenses based on all active filters
    const filteredExpenses = useMemo(() => {
        let result = expenses;

        // Apply payer dropdown filter
        if (selectedPaidBy) {
            result = result.filter(exp => {
                if (exp.type === 'sponsor') {
                    return selectedPaidBy === 'sponsor' || (exp as SponsorExpense).sponsorName === selectedPaidBy;
                } else {
                    return (exp as Expense).payer === selectedPaidBy;
                }
            });
        }

        // Apply quick filter chips
        if (activeQuickFilter === 'involvingMe') {
            result = result.filter(exp => exp.splitBetween.includes(selectedParticipantId));
        } else if (activeQuickFilter === 'createdByMe') {
            result = result.filter(exp => exp.createdBy === selectedParticipantId);
        } else if (activeQuickFilter === 'paidByMe') {
            result = result.filter(exp => {
                if (exp.type === 'sponsor') return false;
                return (exp as Expense).payer === selectedParticipantId;
            });
        }

        return result;
    }, [expenses, selectedPaidBy, activeQuickFilter, selectedParticipantId]);

    // The same plan the payments tab draws, so "3/5 transfers" cannot drift from
    // the five cards it refers to.
    const { settlements, paidCount } = useSettlementPlan(
        expenses, participants, settlementConfig, settlementStatus, meta.shareRatios
    );

    const participantCount = Object.keys(participants).length;
    const totalActualExpenses = actualExpenses.reduce((total, expense) => total + expense.amount, 0);
    const totalSponsorships = sponsorships.reduce((total, sponsor) => total + Math.abs(sponsor.amount), 0);
    const finalExpenses = totalActualExpenses - totalSponsorships;

    // ✅ Get unique payers for filter dropdown
    const uniquePayers = Array.from(new Set([
        ...actualExpenses.map(exp => exp.payer),
        ...sponsorships.map(sponsor => sponsor.sponsorName),
        // Add "sponsor" as a category to show all sponsors
        ...(sponsorships.length > 0 ? ['sponsor'] : [])
    ])).filter(Boolean);

    const handleEditExpense = (expense: Expense | SponsorExpense) => {
        if (expense.type === 'sponsor') {
            setEditingSponsor(expense as SponsorExpense);
            return;
        }
        setEditingExpense(expense as Expense);
    };

    const handleSaveExpense = async (updatedExpense: Expense) => {
        try {
            await updateExpense(eventId, updatedExpense);
            setEditingExpense(null);
        } catch (error) {
            // Error handling is done in the hook
        }
    };

    const handleSaveSponsor = async (updatedSponsor: SponsorExpense) => {
        try {
            // Reuse updateExpense hook for sponsors too
            await updateExpense(eventId, updatedSponsor);
            setEditingSponsor(null);
        } catch (error) {
            // Error handling is done in the hook
        }
    };

    const handleDeleteExpense = async (expenseId: string) => {
        if (window.confirm(t('event.confirmations.deleteItem'))) {
            try {
                await deleteExpense(eventId, expenseId);
            } catch (error) {
                // Error handling is done in the hook
            }
        }
    };

    const canEditExpense = (expense: Expense | SponsorExpense): boolean => {
        // Owner can edit any expense
        if (selectedParticipantId === eventOwnerId) return true;

        // Authors keep full control of their own entries — `editableUntil` is only
        // a hint for other views, not a lock here.
        return expense.createdBy === selectedParticipantId;
    };

    const canDeleteExpense = (expense: Expense | SponsorExpense): boolean => {
        return canEditExpense(expense);
    };

    // Position and date are both carried by `timestamp`, so a drag is written as a
    // new timestamp rather than as a separate ordering field. Only the day the
    // entry landed on is rewritten: entries elsewhere in the list are untouched,
    // and the ones already on that day keep the real times they were recorded at.
    const handleReorder = async ({ order, movedId, targetDayStart }: ReorderPayload) => {
        const moved = order.find(expense => expense.id === movedId);
        if (!moved) return;

        const dayEnd = targetDayStart + DAY_MS - 1;

        // Who lives on that day once the move lands, in the order just dragged.
        const dayMembers = order.filter(expense => (
            expense.id === movedId || startOfDay(expense.timestamp) === targetDayStart
        ));
        // The day's own timestamps, newest first — the same order they appear in
        // above, since a day's entries are always already sorted among themselves.
        const existing = dayMembers
            .filter(expense => expense.id !== movedId)
            .map(expense => expense.timestamp);

        const position = dayMembers.findIndex(expense => expense.id === movedId);

        // A timestamp for the newcomer, placed between the two it was dropped
        // between. Landing on a day of its own keeps its time of day.
        let arrival: number;
        if (existing.length === 0) {
            const timeOfDay = moved.timestamp - startOfDay(moved.timestamp);
            arrival = targetDayStart + timeOfDay;
        } else if (position <= 0) {
            arrival = Math.min(existing[0] + MINUTE_MS, dayEnd);
        } else if (position >= dayMembers.length - 1) {
            arrival = Math.max(existing[existing.length - 1] - MINUTE_MS, targetDayStart);
        } else {
            arrival = Math.floor((existing[position - 1] + existing[position]) / 2);
        }

        const slots = [...existing, arrival].sort((a, b) => b - a);

        // Ties would leave the order undefined the moment the list re-sorts, so
        // equal timestamps are pulled apart by a millisecond.
        for (let i = 1; i < slots.length; i++) {
            if (slots[i] >= slots[i - 1]) {
                slots[i] = slots[i - 1] - 1;
            }
        }

        // Those nudges must never push an entry over midnight — that would file it
        // under a day nobody dropped it on. A day whose entries are all crammed
        // against 00:00 has no room below them to absorb one, so it gets spread
        // across the full day instead: the recorded times are worth less than the
        // entries being on the right day in the right order.
        if (slots[slots.length - 1] < targetDayStart) {
            const step = Math.floor(DAY_MS / (slots.length + 1));
            for (let i = 0; i < slots.length; i++) {
                slots[i] = dayEnd - i * step;
            }
        }

        const changed = dayMembers
            .map((expense, index) => ({ ...expense, timestamp: slots[index] }))
            .filter((expense, index) => expense.timestamp !== dayMembers[index].timestamp);

        if (changed.length === 0) return;

        try {
            await Promise.all(changed.map(expense => updateExpense(eventId, expense)));
        } catch (error) {
            console.error('Error reordering expenses:', error);
        }
    };

    // Helper function to get display name for payer
    const getPayerDisplayName = (payerId: string): string => {
        if (payerId === 'sponsor') {
            return t('event.expenseSection.filter.allSponsors');
        }
        return participants[payerId] || payerId;
    };

    // Four states of one question — "which of these entries are mine?" — so they
    // ride one segmented control rather than three chips of three colours. "All"
    // is the off position, which is also how the filter gets cleared.
    const quickFilterOptions = [
        { value: '' as const, label: t('event.expenseSection.filter.all') },
        { value: 'involvingMe' as const, label: t('event.expenseSection.filter.involvingMe'), icon: faUsers },
        { value: 'createdByMe' as const, label: t('event.expenseSection.filter.createdByMe'), icon: faUserEdit },
        { value: 'paidByMe' as const, label: t('event.expenseSection.filter.paidByMe'), icon: faMoneyBillWave },
    ];

    const clearAllFilters = () => {
        setSelectedPaidBy('');
        setActiveQuickFilter('');
    };

    const hasActiveFilter = selectedPaidBy !== '' || activeQuickFilter !== '';

    return (
        <>
            {/* Summary Header Section */}
            <div className="mb-6">
                <div className="glass-card p-4">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                                <FontAwesomeIcon icon={faChartPie} className="mr-2 text-purple-600"/>
                                {t('event.expenseSection.summary')}
                            </h3>
                            <div className="hidden sm:flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                                <span className="flex items-center">
                                    <FontAwesomeIcon icon={faUsers} className="mr-1"/>
                                    {participantCount}
                                </span>
                                <span className="flex items-center">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-1"/>
                                    {formatDate(meta.createdAt)}
                                </span>
                            </div>
                        </div>
                        <ExportButton
                            expenses={expenses}
                            participants={participants}
                            eventName={meta.name}
                        />
                    </div>

                    {/* Stats */}
                    <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div className="glass-sub-panel px-3 py-2.5">
                                <div className="text-[11px] text-gray-400 dark:text-gray-500 mb-0.5">{t('event.expenseSection.totalExpenses')}</div>
                                <div className="text-base font-bold text-blue-600 dark:text-blue-400">{formatCurrency(totalActualExpenses)}</div>
                            </div>
                            <div className="glass-sub-panel px-3 py-2.5">
                                <div className="text-[11px] text-gray-400 dark:text-gray-500 mb-0.5">{t('event.expenseSection.sponsorships')}</div>
                                <div className="text-base font-bold text-green-600 dark:text-green-400">−{formatCurrency(totalSponsorships)}</div>
                            </div>
                            {/* These two slots used to hold the final total again —
                                it is already spelled out over the list — and an
                                average per head, which divided the whole bill by
                                everyone whether or not they were on any given
                                expense. Both are answered better elsewhere, so the
                                room goes to the two things the summary could not
                                say at all: how much has been recorded, and how much
                                of the settling has actually happened. */}
                            <div className="glass-sub-panel px-3 py-2.5">
                                <div className="text-[11px] text-gray-400 dark:text-gray-500 mb-0.5">{t('event.expenseSection.entries')}</div>
                                <div className="text-base font-bold text-gray-900 dark:text-white">
                                    {actualExpenses.length}
                                    {sponsorships.length > 0 && (
                                        <span className="ml-1.5 text-[11px] font-medium text-green-600 dark:text-green-400">
                                            {t('event.expenseSection.entriesSponsors', { count: sponsorships.length })}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="glass-sub-panel px-3 py-2.5">
                                <div className="text-[11px] text-gray-400 dark:text-gray-500 mb-0.5">{t('event.expenseSection.transfersDone')}</div>
                                {settlements.length === 0 ? (
                                    // Nobody owes anybody — an empty event, or one
                                    // that already came out even.
                                    <div className="text-base font-bold text-gray-300 dark:text-gray-600">—</div>
                                ) : (
                                    <div className={`text-base font-bold ${
                                        paidCount === settlements.length
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-gray-900 dark:text-white'
                                    }`}>
                                        {paidCount}
                                        <span className="font-medium text-gray-400 dark:text-gray-500">/{settlements.length}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 lg:items-start">
                {/* Add Forms — from `lg` up this column stays pinned while the
                    list scrolls past it, so a row further down the list can be
                    answered by typing on the left without scrolling back.
                    Below `lg` the column would stack *above* the list, putting
                    the whole list between one entry and the next, so the form
                    moves into the bottom bar's sheet instead. The closed-event
                    notice is the exception: it renders at every width, because
                    it is the answer to "where did the add form go". */}
                <div className={`lg:col-span-2 min-w-0 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:px-1 lg:-mx-1 lg:pb-4 ${
                    isEventClosed ? '' : 'hidden lg:block'
                }`}>
                    {!isEventClosed ? (
                        <div className="space-y-4">
                            {/* Tab Navigation — the same segmented control as the
                                event tabs, only stretched to fill its column */}
                            <div className="glass-tab-bar flex gap-1">
                                <button
                                    onClick={() => setActiveTab('expense')}
                                    className={`glass-tab flex-1 flex items-center justify-center ${
                                        activeTab === 'expense' ? 'glass-tab-active' : ''
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faReceipt} className="mr-2"/>
                                    {t('event.expenseSection.addExpense')}
                                </button>
                                <button
                                    onClick={() => setActiveTab('sponsor')}
                                    className={`glass-tab flex-1 flex items-center justify-center ${
                                        activeTab === 'sponsor' ? 'glass-tab-active' : ''
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faGift} className="mr-2"/>
                                    {t('event.expenseSection.addSponsor')}
                                </button>
                            </div>

                            {/* Forms */}
                            {activeTab === 'sponsor' ? (
                                <SponsorForm
                                    eventId={eventId}
                                    participants={participants}
                                    shareRatios={meta.shareRatios}
                                    currentUser={selectedParticipantId}
                                    onSponsorAdded={() => {
                                        // Optionally switch back to expense tab after adding
                                        // setActiveTab('expense');
                                    }}
                                />
                            ) : (
                                <ExpenseForm
                                    eventId={eventId}
                                    participants={participants}
                                    shareRatios={meta.shareRatios}
                                    currentUser={selectedParticipantId}
                                    expenses={expenses}
                                />
                            )}
                        </div>
                    ) : (
                        <div
                            className="glass-card p-6">
                            <div className="text-center py-8">
                                <FontAwesomeIcon icon={faLock}
                                                 className="text-6xl text-gray-400 dark:text-gray-600 mb-4"/>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    {t('event.expenseSection.eventClosed.title')}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {t('event.expenseSection.eventClosed.message')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Expense List */}
                <div className="lg:col-span-3 min-w-0">
                    <div
                        className="glass-card">
                        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between gap-3 mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center min-w-0">
                                    <FontAwesomeIcon icon={faReceipt} className="mr-2 text-green-600 flex-shrink-0"/>
                                    <span className="truncate">{t('event.expenseSection.recentActivity')}</span>
                                </h3>
                                {totalActualExpenses > 0 && (
                                    <div className="text-right flex-shrink-0">
                                        <div
                                            className="text-sm text-gray-500 dark:text-gray-400">{t('event.expenseSection.finalExpenses')}</div>
                                        <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                            {formatCurrency(finalExpenses)}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Filters */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="glass-tab-bar inline-flex gap-0.5 !p-0.5 min-w-0 max-w-full overflow-x-auto scrollbar-hide">
                                        {quickFilterOptions.map((opt) => (
                                            <button
                                                key={opt.value || 'all'}
                                                onClick={() => setActiveQuickFilter(opt.value)}
                                                className={`glass-tab-sm flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 ${
                                                    activeQuickFilter === opt.value ? 'glass-tab-active-soft' : ''
                                                }`}
                                            >
                                                {opt.icon && (
                                                    <FontAwesomeIcon icon={opt.icon} className="text-[10px]"/>
                                                )}
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Payer filter — a separate question, so a
                                        separate control, but at the same weight
                                        as the track beside it */}
                                    {uniquePayers.length > 0 && (
                                        <select
                                            value={selectedPaidBy}
                                            onChange={(e) => setSelectedPaidBy(e.target.value)}
                                            aria-label={t('event.expenseSection.filter.filterBy')}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-200 border appearance-none cursor-pointer pr-7 flex-shrink-0 bg-[length:12px] bg-[right_8px_center] bg-no-repeat ${
                                                selectedPaidBy
                                                    ? 'bg-white/95 dark:bg-slate-700/95 border-indigo-200 dark:border-indigo-500/50 text-indigo-600 dark:text-indigo-300 shadow-sm'
                                                    : 'bg-white/50 dark:bg-gray-700/50 border-gray-200/70 dark:border-gray-600/60 text-gray-600 dark:text-gray-300'
                                            }`}
                                            style={{
                                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'/%3E%3C/svg%3E")`
                                            }}
                                        >
                                            <option value="">{t('event.expenseSection.filter.payer')}</option>
                                            {uniquePayers.map(payerId => (
                                                <option key={payerId} value={payerId}>
                                                    {getPayerDisplayName(payerId)}
                                                </option>
                                            ))}
                                        </select>
                                    )}

                                    {/* Only earns its place once both questions
                                        have been answered — a single filter is
                                        undone in the control that set it */}
                                    {selectedPaidBy !== '' && activeQuickFilter !== '' && (
                                        <button
                                            onClick={clearAllFilters}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full transition-colors flex-shrink-0"
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="text-[10px]"/>
                                            {t('event.expenseSection.filter.clearFilter')}
                                        </button>
                                    )}
                                </div>

                                {/* Active filter count indicator */}
                                {hasActiveFilter && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {filteredExpenses.length}/{expenses.length} {expenses.length === 1
                                            ? t('components.expenseList.dateGroup.item')
                                            : t('components.expenseList.dateGroup.items')}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-4 sm:p-6">
                            <ExpenseList
                                expenses={filteredExpenses}
                                participants={participants}
                                shareRatios={meta.shareRatios}
                                currentUser={selectedParticipantId}
                                isAdmin={selectedParticipantId === eventOwnerId}
                                eventId={eventId}
                                onEdit={!isEventClosed ? handleEditExpense : undefined}
                                onDelete={!isEventClosed ? handleDeleteExpense : undefined}
                                onReorder={!isEventClosed ? handleReorder : undefined}
                                canEdit={canEditExpense}
                                canDelete={canDeleteExpense}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Expense Modal */}
            {editingExpense && (
                <ExpenseForm
                    eventId={eventId}
                    participants={participants}
                    shareRatios={meta.shareRatios}
                    currentUser={selectedParticipantId}
                    editExpense={editingExpense}
                    onSave={handleSaveExpense}
                    onCancel={() => setEditingExpense(null)}
                    loading={loading}
                />
            )}

            {/* Edit Sponsor Modal */}
            {editingSponsor && (
                <SponsorForm
                    editSponsor={editingSponsor}
                    eventId={eventId}
                    participants={participants}
                    shareRatios={meta.shareRatios}
                    currentUser={selectedParticipantId}
                    onSave={handleSaveSponsor}
                    onCancel={() => setEditingSponsor(null)}
                    loading={loading}
                />
            )}
        </>
    );
};

export default ExpenseSection;